/**
 * seo.ts — Fonte única de verdade para SEO do site.
 *
 * Centraliza identidade da organização, URLs absolutas, metadados padrão e
 * builders de JSON-LD (schema.org). Consumido pelo componente <Seo /> e pelos
 * scripts de build (sitemap/prerender).
 */

export const SITE_URL = "https://fpf3.com.br";

/** Imagem Open Graph padrão (1200×630). Gerada por scripts/optimize-media.mjs. */
export const DEFAULT_OG_IMAGE = "/og-default.jpg";

/** Identidade institucional — reaproveitada em meta tags e structured data. */
export const ORG = {
  name: "Federação Paraibana de Funcional Fitness",
  shortName: "FPF3",
  legalName: "Federação Paraibana de Funcional Fitness",
  cnpj: "65.974.896/0001-55",
  email: "federacaopbf3@gmail.com",
  logo: "/logos/fpf3.png",
  twitter: "@fpf3pb",
  city: "João Pessoa",
  state: "PB",
  region: "Paraíba",
  country: "BR",
  sport: "Functional Fitness",
  sameAs: [
    "https://www.instagram.com/federacaopbf3/",
    "https://facebook.com/fpf3pb",
    "https://youtube.com/fpf3pb",
  ],
} as const;

/** Resolve um caminho relativo para URL absoluta canônica. */
export function absoluteUrl(pathOrUrl: string): string {
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${SITE_URL}${path}`;
}

// ============================================================================
// JSON-LD builders (schema.org)
// ============================================================================

type JsonLd = Record<string, unknown>;

/** SportsOrganization — a entidade FPF3. Usado na home (sinal de Knowledge Graph). */
export function organizationJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "SportsOrganization",
    "@id": `${SITE_URL}/#organization`,
    name: ORG.name,
    alternateName: ORG.shortName,
    legalName: ORG.legalName,
    url: SITE_URL,
    logo: absoluteUrl(ORG.logo),
    email: ORG.email,
    sport: ORG.sport,
    taxID: ORG.cnpj,
    areaServed: { "@type": "State", name: ORG.region },
    address: {
      "@type": "PostalAddress",
      addressLocality: ORG.city,
      addressRegion: ORG.state,
      addressCountry: ORG.country,
    },
    sameAs: ORG.sameAs,
  };
}

/** WebSite + SearchAction — habilita sitelinks searchbox quando elegível. */
export function websiteJsonLd(): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    url: SITE_URL,
    name: ORG.shortName,
    inLanguage: "pt-BR",
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export interface SportsEventInput {
  name: string;
  startDate: string; // ISO 8601
  endDate?: string;
  city: string;
  venue: string;
  address: string;
  description: string;
  image: string;
  status: "completed" | "coming-info" | "upcoming" | "live" | "cancelled";
  url?: string;
}

/** SportsEvent — cada etapa da liga. */
export function sportsEventJsonLd(e: SportsEventInput): JsonLd {
  const eventStatus =
    e.status === "cancelled"
      ? "https://schema.org/EventCancelled"
      : e.status === "completed"
        ? undefined
        : "https://schema.org/EventScheduled";

  return {
    "@context": "https://schema.org",
    "@type": "SportsEvent",
    name: e.name,
    sport: ORG.sport,
    startDate: e.startDate,
    ...(e.endDate ? { endDate: e.endDate } : {}),
    ...(eventStatus ? { eventStatus } : {}),
    eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
    description: e.description,
    image: absoluteUrl(e.image),
    ...(e.url ? { url: absoluteUrl(e.url) } : {}),
    location: {
      "@type": "Place",
      name: e.venue,
      address: {
        "@type": "PostalAddress",
        streetAddress: e.address,
        addressLocality: e.city,
        addressRegion: ORG.state,
        addressCountry: ORG.country,
      },
    },
    organizer: { "@id": `${SITE_URL}/#organization` },
  };
}

export interface NewsArticleInput {
  title: string;
  description: string;
  image?: string;
  datePublished?: string;
  author?: string;
  url: string;
}

/** NewsArticle — páginas de notícia. */
export function newsArticleJsonLd(a: NewsArticleInput): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: a.title,
    description: a.description,
    ...(a.image ? { image: absoluteUrl(a.image) } : {}),
    ...(a.datePublished ? { datePublished: a.datePublished } : {}),
    inLanguage: "pt-BR",
    mainEntityOfPage: absoluteUrl(a.url),
    author: { "@type": "Organization", name: a.author || ORG.shortName },
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

/** BreadcrumbList — trilha de navegação para sub-páginas. */
export function breadcrumbJsonLd(items: { name: string; path: string }[]): JsonLd {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}
